if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();

        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        // Validate gift card form before submission
        const giftCardForm = this.querySelector("gift-card-custom-form");
        if (giftCardForm) {
          try {
            // Use validateBeforeSubmit() which is the public method
            // Fallback to validateForm() if validateBeforeSubmit doesn't exist (for backwards compatibility)
            const validateMethod =
              giftCardForm.validateBeforeSubmit || giftCardForm.validateForm;
            if (validateMethod && typeof validateMethod === "function") {
              if (!validateMethod.call(giftCardForm)) {
                // Scroll to first error
                const firstError = giftCardForm.querySelector(
                  '[aria-invalid="true"]',
                );
                if (firstError) {
                  firstError.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  firstError.focus();
                }
                // Reset button state
                this.submitButton.classList.remove("loading");
                this.submitButton.removeAttribute("aria-disabled");
                const spinner = this.querySelector(".loading-overlay__spinner");
                if (spinner) spinner.classList.add("hidden");
                return;
              }
            }
          } catch (error) {
            console.error("Error validating gift card form:", error);
            // If validation fails due to error, prevent submission to avoid adding invalid data
            this.submitButton.classList.remove("loading");
            this.submitButton.removeAttribute("aria-disabled");
            const spinner = this.querySelector(".loading-overlay__spinner");
            if (spinner) spinner.classList.add("hidden");
            this.handleErrorMessage(
              "There was an error validating the gift card form. Please try again.",
            );
            return;
          }
        }

        // Store giftCardForm reference for later use
        const giftCardFormRef = giftCardForm;

        function fetchConfig(type = "json") {
          return {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: `application/${type}`,
            },
          };
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner").classList.remove(
          "hidden",
        );

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        // Debug: Log form data to verify gift card properties are included
        if (
          giftCardFormRef &&
          giftCardFormRef.checkboxInput &&
          giftCardFormRef.checkboxInput.checked
        ) {
          console.log(
            "[PRODUCT FORM] Gift card checkbox is checked, adding properties to FormData",
          );
        }

        // Ensure gift card properties are included if checkbox is checked
        if (
          giftCardFormRef &&
          giftCardFormRef.checkboxInput &&
          giftCardFormRef.checkboxInput.checked
        ) {
          // Explicitly add gift card properties to FormData to ensure they're included
          if (
            giftCardFormRef.senderNameInput &&
            giftCardFormRef.senderNameInput.value
          ) {
            formData.set(
              "properties[giftcard_sender_name]",
              giftCardFormRef.senderNameInput.value,
            );
          }
          if (
            giftCardFormRef.senderEmailInput &&
            giftCardFormRef.senderEmailInput.value
          ) {
            formData.set(
              "properties[giftcard_sender_email]",
              giftCardFormRef.senderEmailInput.value,
            );
          }
          if (
            giftCardFormRef.recipientNameInput &&
            giftCardFormRef.recipientNameInput.value
          ) {
            formData.set(
              "properties[giftcard_recipient_name]",
              giftCardFormRef.recipientNameInput.value,
            );
          }
          if (
            giftCardFormRef.recipientEmailInput &&
            giftCardFormRef.recipientEmailInput.value
          ) {
            formData.set(
              "properties[giftcard_recipient_email]",
              giftCardFormRef.recipientEmailInput.value,
            );
          }
          if (
            giftCardFormRef.messageInput &&
            giftCardFormRef.messageInput.value
          ) {
            formData.set(
              "properties[giftcard_message]",
              giftCardFormRef.messageInput.value,
            );
          }
          if (
            giftCardFormRef.deliveryDateInput &&
            giftCardFormRef.deliveryDateInput.value
          ) {
            formData.set(
              "properties[giftcard_delivery_date]",
              giftCardFormRef.deliveryDateInput.value,
            );
          }
          // Add the checkbox property
          formData.set("properties[_send_as_gift]", "yes");
        }

        // Check inventory limits before adding to cart
        const quantity = parseInt(formData.get("quantity") || 1);
        const variantId = formData.get("id");
        const inventoryQuantity = parseInt(
          this.form.querySelector("[data-iventory-quantity]")?.dataset
            .iventoryQuantity || 0,
        );

        if (inventoryQuantity > 0 && quantity > inventoryQuantity) {
          formData.set("quantity", inventoryQuantity);
        }

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            // Clone response for error handling if needed
            const responseClone = response.clone();

            // Check if response is OK
            if (!response.ok) {
              // Try to get error message from response
              const errorText = await responseClone.text();
              let errorMessage = `HTTP error! status: ${response.status}`;
              try {
                const errorJson = JSON.parse(errorText);
                errorMessage =
                  errorJson.description || errorJson.message || errorMessage;
              } catch (e) {
                // If not JSON, use the text or default message
                if (errorText && errorText.length < 200) {
                  errorMessage = errorText;
                }
              }
              throw new Error(errorMessage);
            }

            // Response is OK (200), try to parse as JSON first
            const contentType = response.headers.get("content-type") || "";
            if (
              contentType.includes("application/json") ||
              contentType.includes("application/javascript")
            ) {
              try {
                return await response.json();
              } catch (e) {
                // If JSON parsing fails, try text
                const text = await response.text();
                try {
                  return JSON.parse(text);
                } catch (e2) {
                  // If still not JSON, but response was 200, assume success and fetch cart
                  console.warn(
                    "Response was 200 but not JSON, fetching cart to verify:",
                    text.substring(0, 100),
                  );
                  return await this.handleNonJsonSuccess();
                }
              }
            }

            // If content-type doesn't indicate JSON, try to parse as text first
            const text = await response.text();
            try {
              // Try to parse as JSON even if content-type doesn't say so
              return JSON.parse(text);
            } catch (e) {
              // If parsing fails but response was 200 OK, the item was likely added successfully
              // Check if it's HTML (Shopify sometimes returns HTML with sections)
              if (
                text.trim().startsWith("<") ||
                text.includes("<!DOCTYPE") ||
                text.includes("<html")
              ) {
                console.log(
                  "Response is HTML, item was likely added. Fetching cart to verify.",
                );
                return await this.handleNonJsonSuccess();
              } else {
                // Not HTML and not JSON - might be an error message or empty
                console.warn(
                  "Response was 200 but not JSON or HTML:",
                  text.substring(0, 200),
                );
                // Still try to verify cart was updated
                return await this.handleNonJsonSuccess();
              }
            }
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              // Clear any previous error messages since product was added successfully
              this.handleErrorMessage(false);

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
              });

              // Update cart count - response might be item or cart, fetch full cart for count
              if (response.item_count !== undefined || response.items) {
                // It's a cart object
                updateCartCount(response);
              } else {
                // It's an item object, fetch cart for count update
                fetch(`${routes.cart_url}.js`)
                  .then((res) => res.json())
                  .then((cart) => updateCartCount(cart))
                  .catch((e) =>
                    console.warn("Failed to update cart count:", e),
                  );
              }

              // Update cart notification immediately
              if (this.cart) {
                // Check if response has sections, if not reload page
                if (
                  !response.sections ||
                  Object.keys(response.sections).length === 0
                ) {
                  console.log(
                    "No sections in response, reloading page to show cart",
                  );
                  window.location.reload();
                  return;
                }

                // Clear any previous error messages since product was added successfully
                this.handleErrorMessage(false);

                try {
                  this.cart.renderContents(response);
                  // Cart will only open when user clicks the cart icon, not automatically
                } catch (e) {
                  console.error("Error rendering cart contents:", e);
                  // If rendering fails, reload page to show cart (product was added successfully)
                  // Don't show error message since product was added
                  window.location.reload();
                  return; // Exit early to prevent any further error handling
                }
              }
            }
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    try {
                      if (this.cart && response && response.sections) {
                        this.cart.renderContents(response);
                      }
                    } catch (e) {
                      console.error(
                        "Error rendering cart in modal close handler:",
                        e,
                      );
                      // Don't show error, just reload if needed
                      window.location.reload();
                    }
                  });
                },
                { once: true },
              );
              quickAddModal.hide(true);
            }
          })
          .catch(async (e) => {
            console.error("Error adding to cart:", e);

            // Before showing error, verify if product was actually added
            // Sometimes the error is just a rendering issue, not an add-to-cart failure
            try {
              const cartCheck = await fetch(`${routes.cart_url}.js`);
              if (cartCheck.ok) {
                const cart = await cartCheck.json();
                const variantId = formData.get("id");
                // Check if the item we tried to add is in the cart
                const itemAdded =
                  cart.items &&
                  cart.items.some(
                    (item) =>
                      item.variant_id === parseInt(variantId) ||
                      item.id === parseInt(variantId),
                  );

                if (itemAdded) {
                  // Product was added successfully, just reload to show cart
                  console.log(
                    "Product was added successfully, reloading page to show cart",
                  );
                  window.location.reload();
                  return;
                }
              }
            } catch (checkError) {
              console.warn("Could not verify cart status:", checkError);
            }

            // Only show error if product was NOT added
            const errorMessage =
              e?.message ||
              e?.toString() ||
              "There was an error adding the product to your cart. Please try again.";
            this.handleErrorMessage(errorMessage);
            // Reset button state
            this.submitButton.classList.remove("loading");
            this.submitButton.removeAttribute("aria-disabled");
            const spinner = this.querySelector(".loading-overlay__spinner");
            if (spinner) spinner.classList.add("hidden");
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading-overlay__spinner").classList.add(
              "hidden",
            );
          });
      }

      async handleNonJsonSuccess() {
        // Response was 200 OK but not JSON - fetch cart to verify item was added
        try {
          // First, get the variant ID that was added
          const variantId = this.form.querySelector('[name="id"]')?.value;
          if (!variantId) {
            console.warn("Variant ID not found, using fallback");
          }

          // Fetch the full cart to get the added item
          const cartResponse = await fetch(`${routes.cart_url}.js`);
          if (!cartResponse.ok) {
            throw new Error(`Failed to verify cart: ${cartResponse.status}`);
          }
          const cart = await cartResponse.json();

          if (!cart || !cart.items || cart.items.length === 0) {
            throw new Error("Cart is empty - item may not have been added");
          }

          // Find the item that was just added (usually the last item, or match by variant_id)
          const addedItem = variantId
            ? cart.items.find((item) => item.variant_id === parseInt(variantId))
            : null;
          const itemToReturn = addedItem || cart.items[cart.items.length - 1];

          if (!itemToReturn) {
            throw new Error("Added item not found in cart");
          }

          // If we have sections requested, fetch them
          if (this.cart && this.cart.getSectionsToRender) {
            const sections = this.cart.getSectionsToRender();
            const sectionIds = sections.map((s) => s.id);
            const sectionsUrl = `${window.location.pathname}?sections=${sectionIds.join(",")}`;

            try {
              const sectionsResponse = await fetch(sectionsUrl);
              if (sectionsResponse.ok) {
                const sectionsHtml = await sectionsResponse.text();

                // Parse sections from the HTML response - Shopify returns JSON with sections
                let sectionsData = {};
                try {
                  // Try parsing as JSON first (Shopify sections API returns JSON)
                  const sectionsJson = JSON.parse(sectionsHtml);
                  sections.forEach((section) => {
                    if (sectionsJson[section.id]) {
                      sectionsData[section.id] = sectionsJson[section.id];
                    }
                  });
                } catch (jsonError) {
                  // If not JSON, parse as HTML
                  const parser = new DOMParser();
                  const doc = parser.parseFromString(sectionsHtml, "text/html");
                  sections.forEach((section) => {
                    const sectionElement =
                      doc.querySelector(`#shopify-section-${section.id}`) ||
                      doc.getElementById(section.id);
                    if (sectionElement) {
                      sectionsData[section.id] = sectionElement.outerHTML;
                    }
                  });
                }

                // Return format expected by renderContents: item data + sections
                return {
                  ...itemToReturn,
                  sections: sectionsData,
                };
              }
            } catch (e) {
              console.warn(
                "Failed to fetch sections, returning item data only:",
                e,
              );
            }
          }

          // Return the added item (without sections if fetch failed)
          return itemToReturn;
        } catch (e) {
          console.error("Failed to verify cart after add:", e);
          // Don't throw - item was likely added, just return a minimal response
          // The cart will be updated on next page load
          return {
            id: this.form.querySelector('[name="id"]')?.value || "unknown",
            key: "unknown",
            sections: {},
          };
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
